import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/eoffice/Layout";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { DASHBOARD } from "@/constants/testIds";
import { Plus, FileText, ChevronRight, Circle, Send, Pencil, Files, Trash2, Undo2, CheckCheck, History, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { usePaged, Pager } from "@/components/eoffice/Pager";

const statusChip = (label) => {
  const key = String(label || "").toLowerCase();
  switch (key) {
    case "draft":     return { label: "Draft",     color: "#7A4A17", bg: "#FCEFDE" };
    case "sent":      return { label: "Sent",      color: "#1D3557", bg: "#E4E9F0" };
    case "sent back": return { label: "Sent Back", color: "#1D3557", bg: "#DDE7F3" };
    case "new":       return { label: "New",       color: "#7A1620", bg: "#FBECEE" };
    case "opened":    return { label: "Opened",    color: "#1E6F65", bg: "#E6F4F1" };
    case "closed":    return { label: "Closed",    color: "#1E6F65", bg: "#E6F4F1" };
    default:          return { label: String(label || "—"), color: "#8A8A80", bg: "#F0EFE7" };
  }
};

function fmt(dt) {
  try {
    const d = new Date(dt);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  } catch { return dt; }
}

export default function Dashboard({ scope = "all" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDept, setNewDept] = useState("");
  const [newCategory, setNewCategory] = useState("Others");
  const [newRemark, setNewRemark] = useState("");
  const [newPrevRef, setNewPrevRef] = useState("");
  const [newLaterRef, setNewLaterRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | sent | with_me | closed

  // Row-action dialog state
  const [actionFile, setActionFile] = useState(null); // full file object for the action being performed
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachSelected, setAttachSelected] = useState([]);

  const title = { all: "All Files", my: "My Files", shared: "Inbox", sent: "Sent", approved: "Closed" }[scope];

  const load = async () => {
    try {
      const [f, s, u, d] = await Promise.all([
        api.get(`/files?scope=${scope}`),
        api.get("/stats"),
        api.get("/users"),
        api.get("/documents"),
      ]);
      setFiles(f.data); setStats(s.data); setUsers(u.data); setDocuments(d.data);
    } catch (e) { toast.error("Failed to load files"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/files", {
        name: newName,
        department: newDept,
        category: newCategory,
        remark: newRemark,
        previous_reference: newPrevRef,
        later_reference: newLaterRef,
      });
      toast.success("File created");
      setOpenNew(false);
      setNewName(""); setNewDept(""); setNewCategory("Others"); setNewRemark(""); setNewPrevRef(""); setNewLaterRef("");
      nav(`/files/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Row action helpers ----------------------------------------------------
  const openRename = async (f, e) => {
    e.stopPropagation();
    setActionFile(f); setRenameValue(f.name); setRenameOpen(true);
  };
  const doRename = async () => {
    if (!actionFile) return;
    const name = renameValue.trim();
    if (!name) { toast.error("Name required"); return; }
    try {
      await api.patch(`/files/${actionFile.id}`, { name });
      toast.success("Renamed");
      setRenameOpen(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openSend = async (f, e) => {
    e.stopPropagation();
    setActionFile(f); setSendTo(""); setSendMsg(""); setSendOpen(true);
  };
  const doSend = async () => {
    if (!actionFile || !sendTo) { toast.error("Pick a recipient"); return; }
    try {
      await api.post(`/files/${actionFile.id}/send`, { to_user_id: sendTo, message: sendMsg });
      toast.success("File sent");
      setSendOpen(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openAttach = async (f, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/files/${f.id}`);
      setActionFile(data);
      setAttachSelected(data.attached_docs || []);
      setAttachOpen(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const doAttach = async () => {
    if (!actionFile) return;
    try {
      await api.post(`/files/${actionFile.id}/attach-docs`, { document_ids: attachSelected });
      toast.success("Attachments updated");
      setAttachOpen(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const doDelete = async (f, e) => {
    e.stopPropagation();
    if (!window.confirm(`Permanently delete "${f.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("File deleted");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const doRevoke = async (f, e) => {
    e.stopPropagation();
    if (!window.confirm(`Revoke "${f.name}"? The current recipient will lose access and the file will return to you.`)) return;
    try {
      await api.post(`/files/${f.id}/revoke`);
      toast.success("File revoked");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFile, setHistoryFile] = useState(null);
  const openHistory = async (f, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/files/${f.id}`);
      setHistoryFile(data);
      setHistoryOpen(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Row-level PDF download. For closed files WITH attachments, open a picker
  // so the user can choose single-vs-bundle without opening the file editor.
  const [exportRowOpen, setExportRowOpen] = useState(false);
  const [exportRowFile, setExportRowFile] = useState(null);
  const doDownload = (fileId, mode = "single") => {
    const token = localStorage.getItem("eo_token") || "";
    const path = mode === "bundle" ? "pdf-bundle" : "pdf";
    window.open(`${API_BASE}/files/${fileId}/${path}?token=${encodeURIComponent(token)}`, "_blank");
    setExportRowOpen(false);
  };
  const openDownload = (f, e) => {
    e.stopPropagation();
    const hasAttachments = (f.attached_docs || []).length > 0;
    // Whenever the file has attachments, always let the user pick
    // notesheet-only vs interleaved-bundle — for BOTH drafts and closed files.
    if (hasAttachments) {
      setExportRowFile(f);
      setExportRowOpen(true);
    } else {
      doDownload(f.id, "single");
    }
  };

  const perms = (f) => {
    const isCreator = f.creator_id === user?.id;
    const isHolder = f.current_holder_id === user?.id;
    const notApproved = !f.approved;
    return {
      canRename: isCreator && notApproved,
      canSend: isHolder && notApproved,
      canAttach: notApproved && (isCreator || isHolder),
      canDelete: isCreator && notApproved && f.status === "draft" && isHolder,
      canRevoke: !!f.can_revoke,
    };
  };

  const usersForSend = useMemo(() => users.filter((u) => u.id !== user?.id), [users, user?.id]);

  // Client-side status filtering keyed off the per-user `display_status`
  // (Draft / Sent / Sent Back / New / Opened / Closed). Chips differ by scope:
  //   • my     → All / Draft / Sent / Sent Back / Closed
  //   • shared → All / New / Opened
  //   • all / approved → no chips
  const dispatchLabel = (f) => (f.display_status || (f.approved ? "Closed" : "")).toLowerCase();
  const matchesFilter = (f) => {
    const label = dispatchLabel(f);
    if (scope === "my") {
      switch (statusFilter) {
        case "draft":     return label === "draft";
        case "sent":      return label === "sent";
        case "sent_back": return label === "sent back";
        case "closed":    return !!f.approved;
        default:          return true;
      }
    }
    if (scope === "shared") {
      // Bifurcation stays: files I created (came back to me) vs files others created.
      // Open/unopened is shown as an inline badge on each row (not a filter).
      switch (statusFilter) {
        case "mine":   return f.creator_id === user?.id;
        case "others": return f.creator_id !== user?.id;
        default:       return true;
      }
    }
    return true;
  };
  const filteredFiles = useMemo(() => {
    const list = files.filter(matchesFilter);
    // Unread files (any file sitting in my inbox that I haven't opened yet)
    // bubble to the top on every list. Works for both files sent to me by
    // others and files I created that were sent back to me.
    return [...list].sort((a, b) => {
      const aU = a.is_unopened ? 0 : 1;
      const bU = b.is_unopened ? 0 : 1;
      return aU - bU;
    });
  }, [files, statusFilter, user?.id, scope]);
  const filterCounts = useMemo(() => {
    const by = (fn) => files.filter(fn).length;
    if (scope === "my") return {
      all: files.length,
      draft:     by((f) => dispatchLabel(f) === "draft"),
      sent:      by((f) => dispatchLabel(f) === "sent"),
      sent_back: by((f) => dispatchLabel(f) === "sent back"),
      closed:    by((f) => !!f.approved),
    };
    if (scope === "shared") return {
      all:    files.length,
      mine:   by((f) => f.creator_id === user?.id),
      others: by((f) => f.creator_id !== user?.id),
    };
    return { all: files.length };
  }, [files, user?.id, scope]);
  const paged = usePaged(filteredFiles, `${scope}|${statusFilter}`);

  useEffect(() => { setStatusFilter("all"); }, [scope]);

  // "Create File" nav item routes to /?new=1 — auto-open the dialog on arrival
  // then strip the query so refreshes don't keep re-opening it.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpenNew(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const FILTER_CHIPS = scope === "my"
    ? [
        { key: "all", label: "All" },
        { key: "draft", label: "Draft" },
        { key: "sent", label: "Sent" },
        { key: "sent_back", label: "Sent Back" },
        { key: "closed", label: "Closed" },
      ]
    : scope === "shared"
    ? [
        { key: "all", label: "All" },
        { key: "mine", label: "My Files" },
        { key: "others", label: "Other's Files" },
      ]
    : [];

  return (
    <Layout>
      <div className="px-4 md:px-10 py-6 md:py-10">
        {/* Header */}
        <div className="flex items-end justify-between mb-8 border-b border-[#DCD9D0] pb-6">
          <div>
            <div className="font-mono-eo text-[10px] uppercase tracking-[0.3em] text-[#8A8A80]">// {scope === "all" ? "Overview" : scope}</div>
            <h1 className="font-heading text-4xl md:text-[44px] font-bold leading-none mt-2">{title}</h1>
            <p className="text-[14px] text-[#5C5C54] mt-3">Welcome back, <b className="text-[#0A0A0A]">{user?.name}</b>. Manage documents, approvals and shared work in one place.</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 mb-10 border border-[#DCD9D0]">
          {[
            { k: "my_files", label: "My Files" },
            { k: "shared_with_me", label: "Inbox" },
            { k: "sent", label: "Sent" },
            { k: "approved", label: "Closed" },
            { k: "supporting_docs", label: "Depository Docs" },
          ].map((s, i) => (
            <div key={s.k} data-testid={DASHBOARD.statCard(s.k)} className={`p-5 md:p-6 bg-white border-[#DCD9D0] border-b md:border-b-0 ${i % 2 === 1 ? "border-l" : ""} ${i > 0 ? "md:border-l" : ""}`}>
              <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80]">// {s.label}</div>
              <div className="font-heading text-[36px] font-bold mt-2">{stats[s.k] ?? "-"}</div>
            </div>
          ))}
        </div>

        {/* File list */}
        <div className="border border-[#DCD9D0] bg-white">
          {/* Status filter chips — hidden on the main Dashboard ("all") and Closed views. */}
          {FILTER_CHIPS.length > 0 && (
            <div className="flex items-center gap-2 px-6 py-3 border-b border-[#DCD9D0] bg-white overflow-x-auto eo-scroll">
              <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mr-1 shrink-0">// Filter</div>
              {FILTER_CHIPS.map((c) => {
                const active = statusFilter === c.key;
                const count = filterCounts[c.key] ?? 0;
                return (
                  <button
                    key={c.key}
                    data-testid={`filter-chip-${c.key}`}
                    onClick={() => setStatusFilter(c.key)}
                    className={`px-3 py-1 text-[12px] border rounded-sm transition-colors duration-200 whitespace-nowrap flex items-center gap-1.5 ${
                      active
                        ? "bg-[#1D3557] border-[#1D3557] text-white"
                        : "border-[#DCD9D0] text-[#5C5C54] hover:bg-[#FBFAF3]"
                    }`}
                  >
                    <span>{c.label}</span>
                    <span className={`font-mono-eo text-[10px] ${active ? "text-white/80" : "text-[#8A8A80]"}`}>({count})</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="hidden md:grid grid-cols-[minmax(0,3fr)_1.2fr_1.1fr_1fr_260px_28px] gap-4 px-6 py-3 border-b border-[#DCD9D0] text-[11px] font-mono-eo uppercase tracking-widest text-[#8A8A80] bg-[#FBFAF3]">
            <div>File</div><div>Current Holder</div><div>Updated</div><div>Status</div><div>Actions</div><div></div>
          </div>
          {filteredFiles.length === 0 && (
            <div className="px-6 py-16 text-center">
              <FileText size={28} className="mx-auto text-[#B8B5AA] mb-3" />
              <div className="text-[14px] text-[#5C5C54]">
                {files.length === 0 ? "No files here yet." : `No files match the "${FILTER_CHIPS.find((c) => c.key === statusFilter)?.label}" filter.`}
              </div>
              {files.length === 0 && (scope === "all" || scope === "my") ? (
                <button onClick={() => setOpenNew(true)} className="eo-btn-ghost mt-4 text-[13px]">Create your first file</button>
              ) : (files.length > 0 && statusFilter !== "all") ? (
                <button onClick={() => setStatusFilter("all")} className="eo-btn-ghost mt-4 text-[13px]">Clear filter</button>
              ) : null}
            </div>
          )}
          {paged.items.map((f) => {
            const chip = statusChip(f.display_status || (f.approved ? "Closed" : f.status));
            const p = perms(f);
            const isUnread = !!f.is_unopened;
            return (
              <div
                key={f.id}
                data-testid={DASHBOARD.fileRow(f.id)}
                onClick={() => nav(`/files/${f.id}`)}
                className={`flex flex-col gap-2 px-4 py-4 md:grid md:grid-cols-[minmax(0,3fr)_1.2fr_1.1fr_1fr_260px_28px] md:gap-4 md:px-6 border-b border-[#DCD9D0] md:items-center cursor-pointer transition-colors duration-200 ${isUnread ? "bg-[#FBECEE] hover:bg-[#F8DDE0] border-l-4 border-l-[#E63946]" : "hover:bg-[#FBFAF3]"}`}
              >
                <div className="min-w-0 flex items-start gap-2">
                  {isUnread && <span data-testid={`unread-dot-${f.id}`} className="mt-1.5 w-2 h-2 rounded-full bg-[#E63946] shrink-0" title="Unopened" />}
                  <div className="min-w-0 flex-1">
                    <div data-testid={`file-name-${f.id}`} className={`text-[16px] truncate ${isUnread ? "font-bold font-heading text-[#0A0A0A]" : "font-heading font-semibold text-[#0A0A0A]"}`}>{f.name}</div>
                    <div className="font-mono-eo text-[11px] text-[#8A8A80] mt-0.5 truncate">
                      Created by <b className="text-[#5C5C54]">{f.creator_name}</b> · {f.creator_designation}
                    </div>
                  </div>
                </div>
                <div className="text-[13px] min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1.5 flex-wrap">
                    <span className="md:hidden font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] font-normal">With</span>
                    <span className="truncate">{f.current_holder_name}</span>
                    {/* Sender-side read-receipt: shows to the CREATOR when the current recipient has opened the file. */}
                    {f.opened_by_recipient && f.current_holder_id !== f.creator_id && (
                      <span
                        data-testid={`seen-tick-${f.id}`}
                        title={`Opened by ${f.current_holder_name}`}
                        className="inline-flex items-center gap-0.5 text-[10px] font-mono-eo uppercase tracking-wider text-[#2A9D8F] border border-[#2A9D8F] bg-[#E6F4F1] px-1.5 py-0.5 rounded-sm shrink-0"
                      >
                        <CheckCheck size={10} strokeWidth={2.5} /> Seen
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-[12px] font-mono-eo text-[#5C5C54]">{fmt(f.updated_at)}</div>
                <div>
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 border text-[11px] font-mono-eo uppercase tracking-wider" style={{ borderColor: chip.color, color: chip.color, background: chip.bg }}>
                    <Circle size={7} fill={chip.color} strokeWidth={0} /> {chip.label}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  <RowActionBtn testid={`row-history-${f.id}`} disabled={false} label="View movement history" icon={History} onClick={(e) => openHistory(f, e)} />
                  <RowActionBtn testid={`row-download-${f.id}`} disabled={false} label="Download PDF" icon={FileDown} onClick={(e) => openDownload(f, e)} />
                  <RowActionBtn testid={`row-rename-${f.id}`} disabled={!p.canRename} label="Rename" icon={Pencil} onClick={(e) => openRename(f, e)} />
                  <RowActionBtn testid={`row-send-${f.id}`} disabled={!p.canSend} label="Send" icon={Send} onClick={(e) => openSend(f, e)} />
                  <RowActionBtn testid={`row-attach-${f.id}`} disabled={!p.canAttach} label="Attach docs" icon={Files} onClick={(e) => openAttach(f, e)} />
                  <RowActionBtn testid={`row-revoke-${f.id}`} disabled={!p.canRevoke} label="Revoke — recall from recipient" icon={Undo2} onClick={(e) => doRevoke(f, e)} destructive />
                  <RowActionBtn testid={`row-delete-${f.id}`} disabled={!p.canDelete} label="Delete draft" icon={Trash2} onClick={(e) => doDelete(f, e)} destructive />
                </div>
                <ChevronRight size={16} className="hidden md:block text-[#8A8A80] justify-self-end" data-testid={DASHBOARD.openFileBtn(f.id)} />
              </div>
            );
          })}
          <Pager {...paged} testid="files-pager" />
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-[640px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px]">Create new file</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">File name<span className="text-[#E63946]">*</span></label>
              <input data-testid={DASHBOARD.fileNameInput} className="eo-input" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Procurement Note — Q3" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Department</label>
                <input data-testid="new-file-department" className="eo-input" value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="e.g. Corporate Secretariat" />
              </div>
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Category</label>
                <select data-testid="new-file-category" className="eo-input w-full" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
                  {["Common Office Service","Establishment","Finance Budget Cash and Accounts","General","Hindi","Parliament","Vigilance","Projects","Public Relation","Technical","Training","Others"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Remark</label>
              <input data-testid="new-file-remark" className="eo-input" value={newRemark} onChange={(e) => setNewRemark(e.target.value)} placeholder="Optional short summary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Previous reference</label>
                <input data-testid="new-file-prev-ref" className="eo-input" value={newPrevRef} onChange={(e) => setNewPrevRef(e.target.value)} placeholder="Optional" />
              </div>
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Later reference</label>
                <input data-testid="new-file-later-ref" className="eo-input" value={newLaterRef} onChange={(e) => setNewLaterRef(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <p className="text-[11px] text-[#8A8A80] font-mono-eo">File No will be auto-generated as DCPPL/&lt;dept&gt;/&lt;category&gt;/&lt;DDMMYYYY&gt;-&lt;NNNN&gt; on creation. Created on {new Date().toLocaleString("en-GB", { timeZone: "Asia/Kolkata" })} IST.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setOpenNew(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid={DASHBOARD.createSubmitBtn} disabled={busy || !newName.trim()} onClick={create} className="eo-btn-primary">{busy ? "Creating..." : "Create & Open"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Rename file</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">New name</label>
            <input data-testid="rename-input" className="eo-input" autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doRename()} />
          </div>
          <DialogFooter>
            <button onClick={() => setRenameOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid="rename-submit-btn" disabled={!renameValue.trim()} onClick={doRename} className="eo-btn-primary">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Send &quot;{actionFile?.name}&quot;</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Recipient</label>
              <select data-testid="row-send-user" value={sendTo} onChange={(e) => setSendTo(e.target.value)} className="eo-input">
                <option value="">Select user</option>
                {usersForSend.map((u) => (<option key={u.id} value={u.id}>{u.name} — {u.designation}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Note (optional)</label>
              <textarea data-testid="row-send-msg" value={sendMsg} onChange={(e) => setSendMsg(e.target.value)} className="eo-input min-h-[70px]" placeholder="Any remarks..." />
            </div>
            <p className="text-[11px] text-[#8A8A80] font-mono-eo">Once the recipient opens the file, you will no longer be able to revoke or edit your section.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setSendOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid="row-send-confirm" onClick={doSend} className="eo-btn-primary">Send</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach dialog */}
      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Attach supporting documents</DialogTitle></DialogHeader>
          <div className="max-h-[420px] overflow-auto eo-scroll border border-[#DCD9D0]">
            {documents.length === 0 && <div className="p-6 text-[13px] text-[#8A8A80]">No documents in the depository yet.</div>}
            {documents.map((d) => {
              const checked = attachSelected.includes(d.id);
              return (
                <label key={d.id} className="flex items-start gap-3 px-4 py-3 border-b border-[#DCD9D0] cursor-pointer hover:bg-[#FBFAF3]">
                  <input type="checkbox" checked={checked} onChange={(e) => setAttachSelected((s) => e.target.checked ? [...s, d.id] : s.filter((x) => x !== d.id))} className="mt-1" data-testid={`row-attach-check-${d.id}`} />
                  <div className="min-w-0">
                    <div className="font-semibold text-[14px]">{d.title}</div>
                    <div className="text-[11px] font-mono-eo text-[#8A8A80]">{d.extension.toUpperCase()} · {(d.size / 1024).toFixed(1)} KB · by {d.uploader_name}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <button onClick={() => setAttachOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button onClick={doAttach} className="eo-btn-primary" data-testid="row-attach-save">Save attachments</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Row-level download picker (closed files w/ attachments) */}
      <Dialog open={exportRowOpen} onOpenChange={setExportRowOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-[520px]">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Download {exportRowFile?.approved ? "closed" : "draft"} file</DialogTitle></DialogHeader>
          {exportRowFile && (
            <div className="space-y-3 py-2">
              <div className="text-[12px] font-mono-eo uppercase tracking-widest text-[#8A8A80]">{exportRowFile.name}</div>
              <p className="text-[13px] text-[#5C5C54]">
                This file has <b>{(exportRowFile.attached_docs || []).length}</b> supporting document{(exportRowFile.attached_docs || []).length === 1 ? "" : "s"} attached. Choose what to include:
              </p>
              <button
                type="button"
                data-testid="row-export-single-btn"
                onClick={() => doDownload(exportRowFile.id, "single")}
                className="w-full text-left border border-[#DCD9D0] bg-[#FBFAF3] hover:bg-[#F5F3E9] px-4 py-3 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <FileDown size={18} className="mt-0.5 text-[#1D3557]" />
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-[14px]">Notesheet only (without attachments)</div>
                    <div className="text-[12px] text-[#5C5C54] mt-0.5">{exportRowFile.approved ? "The noting sheet with the CLOSED seal." : "The draft noting sheet with the DRAFT watermark."} No attachments.</div>
                  </div>
                </div>
              </button>
              <button
                type="button"
                data-testid="row-export-bundle-btn"
                onClick={() => doDownload(exportRowFile.id, "bundle")}
                className="w-full text-left border border-[#1D3557] bg-[#F5F3E9] hover:bg-[#EFEEE3] px-4 py-3 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <Files size={18} className="mt-0.5 text-[#1D3557]" />
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-[14px]">Notesheet + hyperlinked attachments</div>
                    <div className="text-[12px] text-[#5C5C54] mt-0.5">Each note is followed by every supporting document that note links to. Any remaining attachments are appended at the end.</div>
                  </div>
                </div>
              </button>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setExportRowOpen(false)} className="eo-btn-ghost">Cancel</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px]">Movement History</DialogTitle>
          </DialogHeader>
          {historyFile && (
            <div className="py-2">
              <div className="border border-[#DCD9D0] bg-[#FBFAF3] px-4 py-3 mb-4">
                <div className="font-heading font-semibold text-[15px] truncate">{historyFile.name}</div>
                <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mt-1">
                  Created by {historyFile.creator_name} · {new Date(historyFile.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })} IST
                </div>
              </div>
              <div className="max-h-[420px] overflow-auto eo-scroll">
                {(historyFile.history || []).length === 0 ? (
                  <div className="p-6 text-center text-[13px] text-[#8A8A80]">No movement history recorded yet.</div>
                ) : (
                  <div className="border-l-2 border-[#DCD9D0] ml-3">
                    {historyFile.history.map((h) => {
                      const dt = new Date(h.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST";
                      let label = "";
                      let dotColor = "#8A8A80";
                      switch (h.type) {
                        case "created":     label = "Created the file"; dotColor = "#1D3557"; break;
                        case "sent":        label = `Sent to ${h.target_name || ""}${h.message ? ` — "${h.message}"` : ""}`; dotColor = "#2A9D8F"; break;
                        case "opened":      label = "Opened the file"; dotColor = "#B8B5AA"; break;
                        case "revoked":     label = `Revoked from ${h.target_name || ""}`; dotColor = "#E63946"; break;
                        case "closed":      label = "Closed the file"; dotColor = "#1D3557"; break;
                        case "reopened":    label = "Reopened the file"; dotColor = "#F4A261"; break;
                        case "attached":    label = "Attached supporting document(s)"; break;
                        case "block_saved": label = "Saved a note section"; break;
                        default:            label = h.type;
                      }
                      return (
                        <div key={h.id} data-testid={`history-row-${h.id}`} className="relative pl-6 pb-4">
                          <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2 border-white" style={{ background: dotColor }} />
                          <div className="text-[13px] text-[#0A0A0A]">
                            <b>{h.actor_name || "Someone"}</b> {label}
                          </div>
                          <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mt-0.5">{dt}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setHistoryOpen(false)} className="eo-btn-primary" data-testid="history-close-btn">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function RowActionBtn({ testid, disabled, label, icon: Icon, onClick, destructive = false }) {
  if (disabled) return null;
  return (
    <button
      data-testid={testid}
      title={label}
      onClick={onClick}
      className={`px-2 py-1 border border-[#DCD9D0] rounded-sm text-[12px] font-medium hover:bg-[#FBFAF3] transition-colors duration-200 ${destructive ? "border-[#E63946] text-[#E63946] hover:bg-[#FBECEE]" : "text-[#5C5C54]"}`}
    >
      <Icon size={13} />
    </button>
  );
}
